import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Save, Percent, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    commission_rate: 0.05,
    service_fee: 0.50,
    min_pickup_delay: 15,
    max_pickup_delay: 120,
    registrations_enabled: true
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch {
      console.error('Error fetching settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        commission_rate: parseFloat(settings.commission_rate),
        service_fee: parseFloat(settings.service_fee),
        min_pickup_delay: parseInt(settings.min_pickup_delay),
        max_pickup_delay: parseInt(settings.max_pickup_delay),
        registrations_enabled: settings.registrations_enabled
      });
      toast.success('Paramètres sauvegardés');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Paramètres globaux">
        <div className="max-w-2xl animate-pulse space-y-4">
          <div className="h-48 bg-gray-200 rounded-2xl" />
          <div className="h-48 bg-gray-200 rounded-2xl" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Paramètres globaux">
      <div className="max-w-2xl space-y-6">
        {/* Commission & Fees */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Percent className="w-5 h-5 text-emerald-500" />
            Commission et frais
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Taux de commission (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={(settings.commission_rate * 100).toFixed(0)}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  commission_rate: parseFloat(e.target.value) / 100 
                })}
                className="mt-1.5 rounded-xl w-32"
              />
              <p className="text-xs text-gray-500 mt-1">
                Commission prélevée sur chaque commande
              </p>
            </div>

            <div>
              <Label>Frais de service (€)</Label>
              <Input
                type="number"
                step="0.10"
                value={settings.service_fee}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  service_fee: parseFloat(e.target.value) 
                })}
                className="mt-1.5 rounded-xl w-32"
              />
              <p className="text-xs text-gray-500 mt-1">
                Frais ajoutés à chaque commande
              </p>
            </div>
          </div>
        </div>

        {/* Pickup Settings */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            Créneaux de retrait
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Délai minimum (minutes)</Label>
              <Input
                type="number"
                value={settings.min_pickup_delay}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  min_pickup_delay: parseInt(e.target.value) 
                })}
                className="mt-1.5 rounded-xl"
              />
            </div>

            <div>
              <Label>Délai maximum (minutes)</Label>
              <Input
                type="number"
                value={settings.max_pickup_delay}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  max_pickup_delay: parseInt(e.target.value) 
                })}
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Système</h2>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Inscriptions activées</p>
              <p className="text-sm text-gray-500">
                Autoriser les nouvelles inscriptions
              </p>
            </div>
            <Switch
              checked={settings.registrations_enabled}
              onCheckedChange={(checked) => setSettings({ 
                ...settings, 
                registrations_enabled: checked 
              })}
            />
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
    </AdminLayout>
  );
}
